import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';

import { I18nService } from '@gruposaltaedu/liv-i18n/core';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap
} from 'rxjs/operators';
import { AppErrorCode } from 'src/app/errors/enums/app-error-code.enum';
import { AppErrorNavigationService } from 'src/app/errors/services/app-error-navigation.service';

import { GeneralService } from './general.service';
import { LivPortfolioErrorResponse } from '../models/liv-portfolio-response-protocol.model';
import { SessionState } from '../store/session/session.state';
import { SetUserCountryAction } from '../store/user/user.actions';
import { UserState } from '../store/user/user.state';

@Injectable({
  providedIn: 'root'
})
export class UserProfileBootstrapService {
  constructor(
    private readonly generalService: GeneralService,
    private readonly i18n: I18nService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly appErrorNavigation: AppErrorNavigationService
  ) {}

  private lastBootstrappedToken: string | null = null;
  private inFlight$?: Observable<boolean | UrlTree>;

  ensureLoaded(url: string): Observable<boolean | UrlTree> {
    const isAuthenticated = this.store.selectSnapshot(
      SessionState.isAuthenticated
    );
    if (!isAuthenticated) {
      this.lastBootstrappedToken = null;
      this.inFlight$ = undefined;
      return of(true);
    }

    const accessToken = this.store.selectSnapshot(SessionState.accessToken);
    if (!accessToken) {
      return of(this.getLanguageValidationResult(url));
    }

    if (this.lastBootstrappedToken === accessToken) {
      return of(this.getLanguageValidationResult(url));
    }

    if (this.inFlight$) {
      return this.inFlight$;
    }

    this.inFlight$ = this.generalService.getAuthUserProfile().pipe(
      switchMap(({ country }) => {
        this.lastBootstrappedToken = accessToken;
        return this.store
          .dispatch(new SetUserCountryAction(country ?? null))
          .pipe(map(() => this.getLanguageValidationResult(url)));
      }),
      catchError((error: unknown) => {
        this.lastBootstrappedToken = null;

        // 401: interceptor já navega para session; devolve UrlTree para o guard
        // não ficar sem emissão (antes EMPTY no interceptor pendurava o shell).
        if (this.isUnauthorizedError(error)) {
          return of(
            this.appErrorNavigation.createUrlTree(AppErrorCode.Session)
          );
        }

        // Perfil é obrigatório (país/idioma). Bloqueia a entrada sem limpar
        // a sessão, para o utilizador poder tentar novamente.
        console.error('Profile bootstrap failed', error);
        return of(
          this.appErrorNavigation.createUrlTree(AppErrorCode.Unexpected)
        );
      }),
      finalize(() => {
        this.inFlight$ = undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.inFlight$;
  }

  private getLanguageValidationResult(url: string): boolean | UrlTree {
    const country = this.store.selectSnapshot(UserState.country);
    if (!country) {
      return true;
    }
    const allowedLanguage = this.i18n.getLanguageFromCountry(country);
    if (!allowedLanguage) {
      return true;
    }

    const parsedUrl = this.router.parseUrl(url);
    const primarySegments = parsedUrl.root.children['primary']?.segments ?? [];
    const currentLanguage = primarySegments[0]?.path ?? null;

    if (currentLanguage && currentLanguage !== allowedLanguage) {
      const reloadScheduled = this.i18n.switchLanguage(allowedLanguage, {
        navigationUrl: url
      });
      return !reloadScheduled;
    }

    this.i18n.syncLanguageFromCountry(country, { navigationUrl: url });
    return true;
  }

  private isUnauthorizedError(error: unknown): boolean {
    const status = (error as LivPortfolioErrorResponse | null)?.status;
    return status === HttpStatusCode.Unauthorized;
  }
}
